import { useState, useEffect } from 'react';

interface PWAUpdateHook {
  updateAvailable: boolean;
  updateApp: () => void;
  dismissUpdate: () => void;
}

export const usePWAUpdate = (): PWAUpdateHook => {
  const [updateAvailable, setUpdateAvailable] = useState(false);
  const [waitingWorker, setWaitingWorker] = useState<ServiceWorker | null>(null);

  useEffect(() => {
    if ('serviceWorker' in navigator) {
      const checkForUpdates = () => {
        navigator.serviceWorker.getRegistration().then((registration) => {
          if (registration) {
            console.log('🔍 Checking for PWA updates...');
            
            // Check for waiting service worker (update available)
            if (registration.waiting) {
              console.log('✅ Update available: Service worker waiting');
              setWaitingWorker(registration.waiting);
              setUpdateAvailable(true);
            }

            // Listen for new service worker installing
            registration.addEventListener('updatefound', () => {
              console.log('🔄 New service worker found, installing...');
              const newWorker = registration.installing;
              if (newWorker) {
                newWorker.addEventListener('statechange', () => {
                  console.log(`🔄 Service worker state: ${newWorker.state}`);
                  if (newWorker.state === 'installed' && navigator.serviceWorker.controller) {
                    // New service worker is installed and ready
                    console.log('✅ Update ready: New service worker installed');
                    setWaitingWorker(newWorker);
                    setUpdateAvailable(true);
                  }
                });
              }
            });

            // Force update check
            registration.update();
          }
        });
      };

      // Check immediately
      checkForUpdates();

      // Check more frequently and on various events
      const handleVisibilityChange = () => {
        if (!document.hidden) {
          console.log('👁️ App visible, checking for updates...');
          checkForUpdates();
        }
      };

      const handleFocus = () => {
        console.log('🎯 App focused, checking for updates...');
        checkForUpdates();
      };

      document.addEventListener('visibilitychange', handleVisibilityChange);
      window.addEventListener('focus', handleFocus);

      // Also check periodically
      const intervalId = setInterval(checkForUpdates, 60000); // Check every minute

      return () => {
        document.removeEventListener('visibilitychange', handleVisibilityChange);
        window.removeEventListener('focus', handleFocus);
        clearInterval(intervalId);
      };
    }
  }, []);

  const updateApp = () => {
    if (waitingWorker) {
      console.log('🚀 User chose to update - activating new service worker...');
      // Tell the waiting service worker to skip waiting and become active
      waitingWorker.postMessage({ type: 'SKIP_WAITING' });
      
      // Listen for the controlling service worker to change
      navigator.serviceWorker.addEventListener('controllerchange', () => {
        console.log('🔄 Service worker updated - reloading page...');
        // Reload the page to get the new version
        window.location.reload();
      });
    } else {
      console.log('❌ No waiting worker found for update');
    }
  };

  const dismissUpdate = () => {
    setUpdateAvailable(false);
  };

  return {
    updateAvailable,
    updateApp,
    dismissUpdate
  };
};
